import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", author: "Thom Gallagher", published: true };

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/blog/posts").then((r) => setPosts(r.data || []));

    useEffect(() => { load(); }, []);

    const save = async () => {
        const isNew = !editing.id;
        try {
            if (isNew) {
                await api.post("/admin/blog/posts", editing);
            } else {
                await api.patch(`/admin/blog/posts/${editing.id}`, editing);
            }
            toast.success("Saved");
            setEditing(null);
            await load();
        } catch {
            toast.error("Could not save");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this post?")) return;
        await api.delete(`/admin/blog/posts/${id}`);
        toast.success("Deleted");
        await load();
    };

    return (
        <div className="p-10" data-testid="admin-blog">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-serif text-3xl text-brand-ink">Journal</h1>
                    <p className="text-sm text-brand-muted mt-1">Create, edit and publish articles.</p>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY })}
                    data-testid="new-post-button"
                    className="bg-brand-ink text-brand-paper px-5 py-2.5 text-sm tracking-wide hover:bg-black transition-colors"
                >
                    + New post
                </button>
            </div>

            {posts.length === 0 ? (
                <div className="text-brand-muted text-sm">No posts yet.</div>
            ) : (
                <div className="border border-brand-line">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-sand">
                            <tr className="text-left text-xs uppercase tracking-wider text-brand-muted">
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Slug</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Updated</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.map((p) => (
                                <tr key={p.id} className="border-t border-brand-line">
                                    <td className="px-4 py-3 text-brand-ink">{p.title}</td>
                                    <td className="px-4 py-3 text-brand-muted">{p.slug}</td>
                                    <td className="px-4 py-3">{p.published ? "Published" : "Draft"}</td>
                                    <td className="px-4 py-3 text-brand-muted">
                                        {new Date(p.updated_at).toLocaleDateString("en-CA")}
                                    </td>
                                    <td className="px-4 py-3 space-x-3">
                                        <button onClick={() => setEditing(p)} className="text-brand-ink underline" data-testid={`edit-post-${p.id}`}>Edit</button>
                                        <button onClick={() => remove(p.id)} className="text-red-700 underline">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4" data-testid="post-editor-modal">
                    <div className="bg-brand-paper border border-brand-line w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-brand-line flex items-center justify-between">
                            <h2 className="font-serif text-2xl text-brand-ink">
                                {editing.id ? "Edit post" : "New post"}
                            </h2>
                            <button onClick={() => setEditing(null)} className="text-brand-muted hover:text-brand-ink">
                                ✕
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <Field label="Title">
                                <input
                                    value={editing.title}
                                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                    data-testid="post-title"
                                    className="w-full bg-transparent border-b border-brand-line py-2 text-brand-ink focus:outline-none focus:border-brand-ink"
                                />
                            </Field>
                            <Field label="Excerpt">
                                <textarea
                                    value={editing.excerpt}
                                    onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                                    rows={2}
                                    data-testid="post-excerpt"
                                    className="w-full bg-transparent border border-brand-line p-3 text-brand-ink focus:outline-none focus:border-brand-ink"
                                />
                            </Field>
                            <Field label="Cover image URL">
                                <input
                                    value={editing.cover_image || ""}
                                    onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                                    data-testid="post-cover"
                                    className="w-full bg-transparent border-b border-brand-line py-2 text-brand-ink focus:outline-none focus:border-brand-ink"
                                />
                            </Field>
                            <Field label="Content (paragraphs separated by blank lines; use **bold**)">
                                <textarea
                                    value={editing.content}
                                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                                    rows={12}
                                    data-testid="post-content"
                                    className="w-full bg-transparent border border-brand-line p-3 text-brand-ink focus:outline-none focus:border-brand-ink font-mono text-sm"
                                />
                            </Field>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={editing.published}
                                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                                    data-testid="post-published"
                                />
                                Published
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button onClick={save} data-testid="post-save-button" className="bg-brand-ink text-brand-paper px-6 py-2.5 text-sm tracking-wide hover:bg-black">Save</button>
                                <button onClick={() => setEditing(null)} className="border border-brand-line px-6 py-2.5 text-sm">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider text-brand-muted mb-2">{label}</span>
            {children}
        </label>
    );
}
